import { create } from 'zustand';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ThemeMode = 'light' | 'dark' | 'custom';

/** One shape controls everything: buttons, images, cards, video */
export type ThemeShape = 'square' | 'rounded' | 'pill';

export interface ThemeColors {
  /** Email outer body background */
  bodyBackground: string;
  /** Email content container background */
  containerBackground: string;
  /** Main text color — used at full opacity for headings, 65% for body text */
  textColor: string;
  /** Accent / button fill color */
  accentColor: string;
  /** Text on top of the accent color */
  accentText: string;
}

export interface ThemePalette {
  id: string;
  name: string;
  colors: ThemeColors;
}

export interface EmailTheme {
  mode: ThemeMode;
  colors: ThemeColors;
  /** Single shape applied to ALL rounded elements: buttons, images, cards, video */
  shape: ThemeShape;
  /** ID of the active preset palette, or null when custom */
  selectedPaletteId: string | null;
}

// ─── Light palettes (from brand palette sheets) ───────────────────────────────

export const LIGHT_PALETTES: ThemePalette[] = [
  {
    id: 'light-sky',
    name: 'Sky',
    colors: {
      bodyBackground: '#EBF8FF',
      containerBackground: '#D7FAFF',
      textColor: '#181D27',
      accentColor: '#3D9DF9',
      accentText: '#FAFAFA',
    },
  },
  {
    id: 'light-navy',
    name: 'Navy',
    colors: {
      bodyBackground: '#E8F5F2',
      containerBackground: '#C5EDE5',
      textColor: '#181D27',
      accentColor: '#1B365D',
      accentText: '#F1F6F5',
    },
  },
  {
    id: 'light-slate',
    name: 'Slate',
    colors: {
      bodyBackground: '#EEF0FF',
      containerBackground: '#C7D2FE',
      textColor: '#181D27',
      accentColor: '#4E5E7F',
      accentText: '#F1F4FF',
    },
  },
  {
    id: 'light-teal',
    name: 'Teal',
    colors: {
      bodyBackground: '#E8F6F3',
      containerBackground: '#C2E8DF',
      textColor: '#181D27',
      accentColor: '#0E738F',
      accentText: '#F6FAF3',
    },
  },
  {
    id: 'light-violet',
    name: 'Violet',
    colors: {
      bodyBackground: '#F3EEFF',
      containerBackground: '#E9D5FF',
      textColor: '#181D27',
      accentColor: '#6D28D9',
      accentText: '#F5F3F7',
    },
  },
  {
    id: 'light-cobalt',
    name: 'Cobalt',
    colors: {
      bodyBackground: '#EBF4FF',
      containerBackground: '#BFDBFE',
      textColor: '#181D27',
      accentColor: '#2436C4',
      accentText: '#F8F8F8',
    },
  },
  {
    id: 'light-classic',
    name: 'Classic',
    colors: {
      bodyBackground: '#F0F2F5',
      containerBackground: '#FFFFFF',
      textColor: '#181D27',
      accentColor: '#2563EB',
      accentText: '#FFFFFF',
    },
  },
  {
    id: 'light-mono',
    name: 'Mono',
    colors: {
      bodyBackground: '#F5F5F5',
      containerBackground: '#FFFFFF',
      textColor: '#111111',
      accentColor: '#111111',
      accentText: '#FFFFFF',
    },
  },
];

// ─── Dark palettes (inverted versions of the same brand palettes) ─────────────

export const DARK_PALETTES: ThemePalette[] = [
  {
    id: 'dark-sky',
    name: 'Sky',
    colors: {
      bodyBackground: '#0A1628',
      containerBackground: '#0D2137',
      textColor: '#D7FAFF',
      accentColor: '#3D9DF9',
      accentText: '#0A1628',
    },
  },
  {
    id: 'dark-navy',
    name: 'Navy',
    colors: {
      bodyBackground: '#0A1420',
      containerBackground: '#0F2035',
      textColor: '#C5EDE5',
      accentColor: '#7FD1B9',
      accentText: '#0A1420',
    },
  },
  {
    id: 'dark-slate',
    name: 'Slate',
    colors: {
      bodyBackground: '#0F1120',
      containerBackground: '#1A1F35',
      textColor: '#C7D2FE',
      accentColor: '#4E5E7F',
      accentText: '#F1F4FF',
    },
  },
  {
    id: 'dark-teal',
    name: 'Teal',
    colors: {
      bodyBackground: '#071820',
      containerBackground: '#0C2530',
      textColor: '#C2E8DF',
      accentColor: '#0E738F',
      accentText: '#F6FAF3',
    },
  },
  {
    id: 'dark-violet',
    name: 'Violet',
    colors: {
      bodyBackground: '#120A20',
      containerBackground: '#1E1035',
      textColor: '#E9D5FF',
      accentColor: '#6D28D9',
      accentText: '#F5F3F7',
    },
  },
  {
    id: 'dark-cobalt',
    name: 'Cobalt',
    colors: {
      bodyBackground: '#080E20',
      containerBackground: '#0E1835',
      textColor: '#BFDBFE',
      accentColor: '#2436C4',
      accentText: '#F8F8F8',
    },
  },
  {
    id: 'dark-classic',
    name: 'Classic',
    colors: {
      bodyBackground: '#0F172A',
      containerBackground: '#1E293B',
      textColor: '#F1F5F9',
      accentColor: '#3B82F6',
      accentText: '#FFFFFF',
    },
  },
  {
    id: 'dark-mono',
    name: 'Mono',
    colors: {
      bodyBackground: '#0A0A0A',
      containerBackground: '#1A1A1A',
      textColor: '#F5F5F5',
      accentColor: '#F5F5F5',
      accentText: '#111111',
    },
  },
];

export const ALL_PALETTES = [...LIGHT_PALETTES, ...DARK_PALETTES];

export const DEFAULT_SHAPE: ThemeShape = 'rounded';

/** Fallback used by buildEmailHtml when no theme is provided */
export const LIGHT_THEME_COLORS: ThemeColors = {
  bodyBackground: '#F0F2F5',
  containerBackground: '#FFFFFF',
  textColor: '#181D27',
  accentColor: '#2563EB',
  accentText: '#FFFFFF',
};

// ─── Derived color helpers ────────────────────────────────────────────────────

export const deriveSecondaryText = (textColor: string): string => {
  const hex = textColor.replace('#', '');
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},0.65)`;
  }
  return textColor;
};

export const deriveCardBackground = (containerBackground: string, textColor: string): string => {
  const hex = textColor.replace('#', '');
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},0.06)`;
  }
  return containerBackground;
};

export const deriveCardBorder = (textColor: string): string => {
  const hex = textColor.replace('#', '');
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},0.12)`;
  }
  return textColor;
};

// ─── Border-radius helpers ────────────────────────────────────────────────────

export const SHAPE_RADIUS: Record<ThemeShape, string> = {
  square: '0px',
  rounded: '10px',
  pill: '9999px',
};

export const IMAGE_SHAPE_RADIUS: Record<ThemeShape, string> = {
  square: '0px',
  rounded: '12px',
  pill: '24px',
};

// ─── Block color scheme helpers ───────────────────────────────────────────────

/**
 * Given the current theme state, resolve the actual background and text colors
 * for a header/footer block based on its colorScheme setting.
 *
 * - light/dark theme mode: returns the light or dark variant of the active palette
 * - custom theme mode: returns the theme's custom colors (or block-level overrides)
 */
export const resolveBlockColors = (
  themeMode: ThemeMode,
  themeColors: ThemeColors,
  selectedPaletteId: string | null,
  colorScheme: 'light' | 'dark' | 'custom',
  customBg: string,
  customText: string,
): { bg: string; textColor: string } => {
  // Custom scheme always uses the block-level overrides, falling back to theme colors
  if (colorScheme === 'custom' || themeMode === 'custom') {
    return {
      bg: customBg || themeColors.containerBackground,
      textColor: customText || themeColors.textColor,
    };
  }

  // For light/dark theme modes, find the paired palette variant
  const paletteName = selectedPaletteId?.replace(/^(light|dark)-/, '') ?? 'classic';
  const targetId = `${colorScheme}-${paletteName}`;
  const palette = ALL_PALETTES.find((p) => p.id === targetId);
  if (palette) {
    return { bg: palette.colors.containerBackground, textColor: palette.colors.textColor };
  }
  // Fallback: use theme colors directly
  return { bg: themeColors.containerBackground, textColor: themeColors.textColor };
};

// ─── Store ────────────────────────────────────────────────────────────────────

interface EmailThemeState extends EmailTheme {
  /** Select a preset palette (sets mode to light or dark automatically) */
  selectPalette: (paletteId: string) => void;
  /** Switch to custom mode and update individual color fields */
  updateColors: (patch: Partial<ThemeColors>) => void;
  setShape: (shape: ThemeShape) => void;
  /** Switch the mode tab without changing the palette selection */
  setMode: (mode: ThemeMode) => void;
}

const DEFAULT_PALETTE = LIGHT_PALETTES.find((p) => p.id === 'light-classic') ?? LIGHT_PALETTES[0]!;

export const useEmailThemeStore = create<EmailThemeState>((set) => ({
  mode: 'light',
  colors: { ...DEFAULT_PALETTE.colors },
  shape: DEFAULT_SHAPE,
  selectedPaletteId: DEFAULT_PALETTE.id,

  selectPalette: (paletteId) => {
    const palette = ALL_PALETTES.find((p) => p.id === paletteId);
    if (!palette) return;
    const mode: ThemeMode = paletteId.startsWith('dark-') ? 'dark' : 'light';
    set({ mode, colors: { ...palette.colors }, selectedPaletteId: paletteId });
  },

  updateColors: (patch) =>
    set((state) => ({
      colors: { ...state.colors, ...patch },
      mode: 'custom',
      selectedPaletteId: null,
    })),

  setShape: (shape) => set({ shape }),

  setMode: (mode) =>
    set((prev) => {
      if (mode === 'custom') return { mode, selectedPaletteId: null };
      // When switching to light/dark tab, keep current palette if it already matches the mode
      const currentIsCorrectMode =
        (mode === 'light' && prev.selectedPaletteId?.startsWith('light-')) ||
        (mode === 'dark' && prev.selectedPaletteId?.startsWith('dark-'));
      if (currentIsCorrectMode) return { mode };
      // Default to the "Classic" palette (white/blue) when switching modes
      const defaultPalette = mode === 'light'
        ? (LIGHT_PALETTES.find((p) => p.id === 'light-classic') ?? LIGHT_PALETTES[0]!)
        : (DARK_PALETTES.find((p) => p.id === 'dark-classic') ?? DARK_PALETTES[0]!);
      return { mode, colors: { ...defaultPalette.colors }, selectedPaletteId: defaultPalette.id };
    }),
}));
